/**
 * セレクトボックス
 */

import { useDropdownPosition } from '@src/hooks/useDropdownPosition';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type SelectorItem = {
  id: string;
  name: string;
};

type Props = {
  items: SelectorItem[];
  onSelect?: (id: string) => void;
  initialValue?: string | number | null | undefined;
  size?: 's' | 'm';
  error?: boolean;
  'data-testid'?: string;
  disabled?: boolean;
  className?: string;
  optionClassName?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  containerClassName?: string;
  showPlaceholderOption?: boolean;
};

const CONSTANTS = {
  MAX_HEIGHT: 200,
  DROPDOWN_MAX_HEIGHT: '200px',
  EMPTY_VALUE: '',
} as const;

const Select = forwardRef<HTMLDivElement, Props>(
  (
    {
      items,
      onSelect,
      initialValue,
      size = 's',
      error = false,
      disabled = false,
      placeholder = '選択してください',
      optionClassName,
      containerClassName,
      showPlaceholderOption = true,
      ...props
    },
    ref,
  ) => {
    const normalizeValue = useCallback(
      (value: string | number | null | undefined): string | undefined => {
        if (value === null || value === undefined || value === '') {
          return undefined;
        }
        return value.toString();
      },
      [],
    );

    const [selectedValue, setSelectedValue] = useState<string | undefined>(
      normalizeValue(initialValue),
    );
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [isKeyboardMode, setIsKeyboardMode] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { position, below, calculatePosition } = useDropdownPosition(CONSTANTS.MAX_HEIGHT);

    const getSelectedIndex = useCallback((options: typeof allOptions, value?: string) => {
      return options.findIndex(
        (option) => option.id === value || (option.id === CONSTANTS.EMPTY_VALUE && !value),
      );
    }, []);

    const allOptions = useMemo(
      () =>
        showPlaceholderOption
          ? [{ id: CONSTANTS.EMPTY_VALUE, name: placeholder }, ...items]
          : items,
      [items, placeholder, showPlaceholderOption],
    );

    useEffect(() => {
      setSelectedValue(normalizeValue(initialValue));
    }, [initialValue, normalizeValue]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const handleSelect = useCallback(
      (value: string) => {
        if (disabled) {
          return;
        }
        if (!showPlaceholderOption && value === CONSTANTS.EMPTY_VALUE) {
          return;
        }
        setSelectedValue(value);
        onSelect?.(value);
        setIsOpen(false);
        setHoveredIndex(-1);
        setIsKeyboardMode(false);
      },
      [disabled, onSelect, showPlaceholderOption],
    );

    const toggleDropdown = useCallback(() => {
      if (disabled) {
        return;
      }
      if (!isOpen) {
        calculatePosition(dropdownRef);
      }
      setIsOpen(!isOpen);
      setHoveredIndex(-1);
      setIsKeyboardMode(false);
    }, [disabled, isOpen, calculatePosition]);

    const handleArrowDown = useCallback(() => {
      setIsKeyboardMode(true);
      if (!isOpen) {
        setIsOpen(true);
        calculatePosition(dropdownRef);
        const selectedIndex = getSelectedIndex(allOptions, selectedValue);
        setHoveredIndex(selectedIndex >= 0 ? selectedIndex : 0);
      } else {
        setHoveredIndex((prev) => {
          if (prev === -1) {
            const selectedIndex = getSelectedIndex(allOptions, selectedValue);
            const startIndex = showPlaceholderOption ? 1 : 0;
            return Math.min(
              selectedIndex >= 0 ? selectedIndex + 1 : startIndex,
              allOptions.length - 1,
            );
          }
          return Math.min(prev + 1, allOptions.length - 1);
        });
      }
    }, [
      isOpen,
      calculatePosition,
      getSelectedIndex,
      allOptions,
      selectedValue,
      showPlaceholderOption,
    ]);

    const handleArrowUp = useCallback(() => {
      setIsKeyboardMode(true);
      if (!isOpen) {
        setIsOpen(true);
        calculatePosition(dropdownRef);
        const selectedIndex = getSelectedIndex(allOptions, selectedValue);
        setHoveredIndex(selectedIndex >= 0 ? selectedIndex : allOptions.length - 1);
      } else {
        setHoveredIndex((prev) => {
          if (prev === -1) {
            const selectedIndex = getSelectedIndex(allOptions, selectedValue);
            const minIndex = showPlaceholderOption ? 0 : 0;
            return Math.max(
              selectedIndex >= 0 ? selectedIndex - 1 : allOptions.length - 2,
              minIndex,
            );
          }
          const minIndex = showPlaceholderOption ? 0 : 0;
          return Math.max(prev - 1, minIndex);
        });
      }
    }, [
      isOpen,
      calculatePosition,
      getSelectedIndex,
      allOptions,
      selectedValue,
      showPlaceholderOption,
    ]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (disabled) {
          return;
        }

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            handleArrowDown();
            break;
          case 'ArrowUp':
            e.preventDefault();
            handleArrowUp();
            break;
          case 'Enter':
            e.preventDefault();
            if (isOpen && hoveredIndex >= 0) {
              handleSelect(allOptions[hoveredIndex].id);
            } else {
              toggleDropdown();
            }
            break;
          case 'Escape':
            if (isOpen) {
              setIsOpen(false);
              setHoveredIndex(-1);
              setIsKeyboardMode(false);
            }
            break;
        }
      },
      [
        disabled,
        isOpen,
        hoveredIndex,
        allOptions,
        handleSelect,
        toggleDropdown,
        handleArrowDown,
        handleArrowUp,
      ],
    );

    const selectedItem = useMemo(
      () => items.find((item) => item.id === selectedValue),
      [items, selectedValue],
    );

    const displayText = selectedItem?.name || placeholder;

    const getOptionClassName = useCallback(
      (optionIndex: number, optionId: string) => {
        const isSelected =
          selectedValue === optionId || (optionId === CONSTANTS.EMPTY_VALUE && !selectedValue);
        const isHovered = hoveredIndex === optionIndex;

        return `
          cursor-pointer px-4 py-[10px] text-sm transition-colors
          ${
            isHovered
              ? 'bg-primary text-white'
              : isSelected && hoveredIndex === -1
                ? 'bg-primary text-white'
                : isKeyboardMode
                  ? ''
                  : 'hover:bg-primary hover:text-white'
          }
          ${optionClassName ?? ''}
          ${size === 's' ? 'py-[8px] lg:py-[6px]' : ''}
          ${size === 'm' ? 'py-[12px] lg:py-[10px]' : ''}
        `;
      },
      [selectedValue, size, hoveredIndex, isKeyboardMode, optionClassName],
    );

    return (
      <div
        ref={ref}
        className={`relative ${containerClassName}`}
      >
        <div
          ref={dropdownRef}
          onClick={toggleDropdown}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role='combobox'
          aria-expanded={isOpen}
          aria-haspopup='listbox'
          data-testid={props['data-testid']}
          className={`
            flex cursor-pointer appearance-none items-center rounded-[2px] border border-solid border-gray-250 bg-white bg-down bg-[center_right_12px] bg-no-repeat py-[10px] pl-4 pr-7 text-sm lg:text-base
            ${size === 's' ? ' py-[10px] lg:py-[8px]' : ''}
            ${size === 'm' ? ' py-[14px] lg:py-[12px]' : ''}
            ${error ? '!border-warning-500 !bg-warning-200/70' : ''}
            ${disabled ? '!cursor-not-allowed !bg-gray-200 bg-down-gray' : ''}
            ${props.className ?? ''}
          `}
          style={props.style}
        >
          {displayText}
        </div>

        {isOpen && !disabled && (
          <div
            role='listbox'
            className={`
              absolute z-[9999] mt-1 w-full rounded-[2px] border border-solid border-gray-250 bg-white
              ${below ? 'top-full' : 'bottom-full mb-1'}
            `}
            style={{
              maxHeight: position?.maxHeight || CONSTANTS.DROPDOWN_MAX_HEIGHT,
            }}
          >
            <div className='max-h-48 overflow-y-auto'>
              {showPlaceholderOption && (
                <div
                  id={`option-${CONSTANTS.EMPTY_VALUE}`}
                  role='option'
                  aria-selected={!selectedValue || selectedValue === CONSTANTS.EMPTY_VALUE}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(CONSTANTS.EMPTY_VALUE);
                  }}
                  onMouseEnter={() => {
                    setIsKeyboardMode(false);
                    setHoveredIndex(0);
                  }}
                  onMouseLeave={() => setHoveredIndex(-1)}
                  className={getOptionClassName(0, CONSTANTS.EMPTY_VALUE)}
                >
                  {placeholder}
                </div>
              )}

              {items && items.length > 0 ? (
                items.map((option, index) => (
                  <div
                    key={option.id}
                    id={`option-${option.id}`}
                    role='option'
                    aria-selected={selectedValue === option.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option.id);
                    }}
                    onMouseEnter={() => {
                      setIsKeyboardMode(false);
                      setHoveredIndex(showPlaceholderOption ? index + 1 : index);
                    }}
                    onMouseLeave={() => setHoveredIndex(-1)}
                    className={getOptionClassName(
                      showPlaceholderOption ? index + 1 : index,
                      option.id,
                    )}
                  >
                    {option.name}
                  </div>
                ))
              ) : (
                <div className='cursor-not-allowed px-4 py-[10px] text-sm text-gray-400'>
                  データがありません
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
